import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { generateJSON } from '@tiptap/html';
import StarterKit from '@tiptap/starter-kit';
import { common, createLowlight } from 'lowlight';
import { Marked } from 'marked';

const lowlight = createLowlight(common);

const ENTITY_TYPES = [
  'plan',
  'convention',
  'completionReport',
  'postMortem',
  'coAction',
  'codeReview',
  'document',
  'skill',
  'LINEAR_ISSUE',
  'GITHUB_ISSUE',
  'GITHUB_PR',
  'GITLAB_ISSUE',
  'GITLAB_MERGE_REQUEST',
  'BITBUCKET_ISSUE',
  'BITBUCKET_PR',
  'SENTRY_ISSUE',
];

const ENTITY_REF_HREF_PATTERN = new RegExp('^(' + ENTITY_TYPES.join('|') + '):');

const EXTERNAL_ENTITY_TYPES = new Set([
  'LINEAR_ISSUE',
  'GITHUB_ISSUE',
  'GITHUB_PR',
  'GITLAB_ISSUE',
  'GITLAB_MERGE_REQUEST',
  'BITBUCKET_ISSUE',
  'BITBUCKET_PR',
  'SENTRY_ISSUE',
]);

for (const externalType of EXTERNAL_ENTITY_TYPES) {
  if (!ENTITY_TYPES.includes(externalType)) {
    throw new Error(`External entity type is not registered: ${externalType}`);
  }
}

const PLACEHOLDER_PREFIX = 'https://__entity_ref__/';

// Markdown 전처리: 소문자 프로토콜 entity ref 링크를 sanitization-safe placeholder URL로 치환
const ENTITY_LINK_MD_PATTERN = new RegExp(
  '\\[([^\\]]+)\\]\\((' + ENTITY_TYPES.join('|') + '):((?:[^()\\s]|\\([^()]*\\))*)\\)',
  'g',
);

function escapeEntityRefsInMarkdown(markdown) {
  return markdown.replace(ENTITY_LINK_MD_PATTERN, (_match, label, type, rest) => {
    const encoded = encodeURIComponent(type + ':' + rest);
    return `[${label}](${PLACEHOLDER_PREFIX}${encoded})`;
  });
}

function resolveEntityHref(href) {
  if (href.startsWith(PLACEHOLDER_PREFIX)) {
    return decodeURIComponent(href.slice(PLACEHOLDER_PREFIX.length));
  }
  return href;
}

function entityRefToProviderUrl(entityType, entityId) {
  switch (entityType) {
    case 'GITHUB_ISSUE': {
      const m = entityId.match(/^(.+?)#(\d+)$/);
      if (m) return `https://github.com/${m[1]}/issues/${m[2]}`;
      break;
    }
    case 'GITHUB_PR': {
      const m = entityId.match(/^(.+?)#(\d+)$/);
      if (m) return `https://github.com/${m[1]}/pull/${m[2]}`;
      break;
    }
    case 'GITLAB_ISSUE': {
      const m = entityId.match(/^(.+?)#(\d+)$/);
      if (m) return `https://gitlab.com/${m[1]}/-/issues/${m[2]}`;
      break;
    }
    case 'GITLAB_MERGE_REQUEST': {
      const m = entityId.match(/^(.+?)!(\d+)$/);
      if (m) return `https://gitlab.com/${m[1]}/-/merge_requests/${m[2]}`;
      break;
    }
    case 'BITBUCKET_ISSUE': {
      const m = entityId.match(/^(.+?)#(\d+)$/);
      if (m) return `https://bitbucket.org/${m[1]}/issues/${m[2]}`;
      break;
    }
    case 'BITBUCKET_PR': {
      const m = entityId.match(/^(.+?)#(\d+)$/);
      if (m) return `https://bitbucket.org/${m[1]}/pull-requests/${m[2]}`;
      break;
    }
  }
  return null;
}

function createEntityRefNode(entityType, originalHref, label) {
  const rest = originalHref.slice(entityType.length + 1);
  const attrs = { entityType, entityId: rest, label: label || '', path: '', htmlUrl: '' };

  if (entityType === 'convention' || entityType === 'skill') {
    const colonIdx = rest.indexOf(':');
    if (colonIdx !== -1) {
      attrs.entityId = rest.slice(0, colonIdx);
      attrs.path = rest.slice(colonIdx + 1);
    }
  }

  if (EXTERNAL_ENTITY_TYPES.has(entityType)) {
    attrs.htmlUrl = entityRefToProviderUrl(entityType, rest) || originalHref;
  }

  return { type: 'entityReference', attrs };
}

function processContent(nodes) {
  return nodes.map((node) => {
    if (node.type === 'text' && Array.isArray(node.marks)) {
      const linkMark = node.marks.find((m) => m.type === 'link');
      if (linkMark && linkMark.attrs?.href) {
        const resolved = resolveEntityHref(linkMark.attrs.href);
        const match = resolved.match(ENTITY_REF_HREF_PATTERN);
        if (match) {
          return createEntityRefNode(match[1], resolved, node.text);
        }
      }
    }

    if (Array.isArray(node.content)) {
      return { ...node, content: processContent(node.content) };
    }

    return node;
  });
}

function transformLinksToEntityReferences(doc) {
  if (!doc || !Array.isArray(doc.content)) return doc;
  return { ...doc, content: processContent(doc.content) };
}

const extensions = [
  StarterKit.configure({ codeBlock: false }),
  Table.configure({ resizable: true }),
  TableRow,
  TableHeader,
  TableCell,
  TaskList,
  TaskItem.configure({ nested: true }),
  CodeBlockLowlight.configure({ lowlight }),
];

// GFM 태스크 리스트(`- [x] ...`)를 Tiptap taskList/taskItem 모양의 HTML로 낸다.
//
// marked 기본 렌더러는 `<ul><li><input type="checkbox">...</li></ul>`를 내는데, Tiptap의
// TaskList/TaskItem parseHTML은 `data-type` 속성으로만 매칭하므로 그대로 두면 평범한 bulletList가
// 되어 **체크 상태와 체크리스트 종류가 함께 사라진다**. 저장이 마크다운을 경유하는 경로
// (본문 저장·실행 정책 저장)에서는 본문을 건드리지 않아도 이 손실이 일어난다.
//
// 항목이 전부 task일 때만 taskList로 낸다. 섞인 목록은 Tiptap에서도 한 노드에 담을 수 없으므로
// 기본 렌더러(`false` 반환)로 넘긴다.
const taskListRenderer = {
  list(token) {
    if (token.ordered || token.items.length === 0 || !token.items.every((item) => item.task)) {
      return false;
    }
    let body = '';
    for (const item of token.items) {
      body += this.listitem(item);
    }
    return `<ul data-type="taskList">\n${body}</ul>\n`;
  },
  listitem(token) {
    if (!token.task) return false;
    const body = this.parser.parse(token.tokens, !!token.loose);
    return `<li data-type="taskItem" data-checked="${token.checked ? 'true' : 'false'}">${body}</li>\n`;
  },
};

// 전역 `marked`를 건드리지 않는다 — 같은 프로세스의 다른 마크다운 소비자에게 이 렌더러가 새면 안 된다.
const marked = new Marked({ gfm: true }, { renderer: taskListRenderer });

const isRecord = (value) => {
  return typeof value === 'object' && value !== null;
};

export const isTiptapDocJson = (content) => {
  try {
    const parsed = JSON.parse(content);
    if (!isRecord(parsed)) {
      return false;
    }

    return parsed.type === 'doc' && Array.isArray(parsed.content);
  } catch {
    return false;
  }
};

export const markdownToTiptap = async (markdown) => {
  const escaped = escapeEntityRefsInMarkdown(markdown);
  const html = await marked.parse(escaped, { gfm: true });
  const json = generateJSON(html, extensions);
  return transformLinksToEntityReferences(json);
};

export const normalizeMarkdownToTiptap = async (content) => {
  if (isTiptapDocJson(content)) {
    return content;
  }

  const json = await markdownToTiptap(content);
  return JSON.stringify(json);
};
